import { useEffect, useRef } from 'react'
import { useLogto } from '@logto/react'
import { setTokenProvider, clearAccessToken, setApiResource } from '@/services/tokenBridge'
import { isLogtoEnabled, getLogtoApiResource } from '@/providers/LogtoProviderWrapper'

/**
 * Connect Logto's getAccessToken to API service.
 *
 * Must be used inside LogtoProvider.
 * Synchronously sets token provider to ensure availability before API calls.
 */
export function useLogtoToken() {
  const logto = useLogto()
  const initializedRef = useRef(false)

  // Synchronously set token provider (on first render)
  // Ensures availability before child component useEffects run
  if (!initializedRef.current && isLogtoEnabled()) {
    initializedRef.current = true
    setTokenProvider(logto.getAccessToken)
  }

  useEffect(() => {
    if (!isLogtoEnabled()) return

    // Update when getAccessToken changes
    setTokenProvider(logto.getAccessToken)

    // Set API Resource
    const resource = getLogtoApiResource()
    if (resource) {
      setApiResource(resource)
    }

    // Cleanup on unmount
    return () => {
      clearAccessToken()
    }
  }, [logto.getAccessToken])
}

// Hook to check authentication state
export function useLogtoAuth() {
  const logto = useLogto()

  if (!isLogtoEnabled()) {
    // Development mode: always authenticated
    return {
      isAuthenticated: true,
      isLoading: false,
      signIn: () => Promise.resolve(),
      signOut: () => Promise.resolve(),
      getAccessToken: () => Promise.resolve(undefined),
    }
  }

  return {
    isAuthenticated: logto.isAuthenticated,
    isLoading: logto.isLoading,
    signIn: (redirectUri: string) => logto.signIn(redirectUri),
    signOut: (postLogoutRedirectUri?: string) => logto.signOut(postLogoutRedirectUri),
    getAccessToken: logto.getAccessToken,
  }
}
